import { App as AppWrapper, ConfigProvider, message } from "antd";
import enUS from "antd/locale/en_US";


import store, { persistor, RootState, useAppSelector } from "./store";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
// import { ErrorBoundary } from "react-error-boundary";
import { Suspense, lazy, useEffect, useState } from "react";
import { initializeCapacitor } from "./capacitor";
import {
  getDecryptedToken,
  getDecryptedUserData,
  getLoggedInUserId,
} from "./helpers/bycrypt";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  clearExceptionUserInfo,
  ExceptionBoundary,
  setExceptionUserInfo,
} from "react-3rddigital-exception-tracking";
import { NativeExceptionHandler } from "capacitor-3rddigital-exception-tracking";
import "./helpers/i18n.ts";
// //CSS
import "./Assets/CSS/custom.css";
import "./Assets/CSS/style.css";
import "./Assets/CSS/component.css";

// Auth CSS
import "./Auth/CSS/style.css";

// Component CSS
import "./Assets/CSS/component.css";

// Admin CSS
import "./Admin/CSS/style.css";
import "./Admin/CSS/admin.css";
import "./Admin/CSS/summary.css";

import { ErrorFallbackPage } from "./helpers/ErrorFallbackPage";
import { SiteLoader } from "./Components/Components";
import { FeatureGuard } from "./Components/FeatureGuard";
import ToastHandler from "./helpers/ToastHandler";
import { sideMenuBarData } from "./Assets/navigation";
import { LocalDebug } from "./Components/LocalDebug";
import { alias, isInvoiceFeatureEnabled, ROLE } from "./helpers/utils";
import { initAppLifecycleScheduler } from "./helpers/appLifecycleScheduler";
import {
  initDeviceTracking,
  trackDeviceStart,
} from "./helpers/deviceTracking";
import CryptoFunction from "./helpers/CryptoFunction";
import PartsRestriction from "./Admin/Pages/PartsRestriction/PartsRestriction";
import ImportPartsRestrictions from "./Admin/Pages/PartsRestriction/ImportPartsRestrictions";
import SubAdmins from "./Admin/Pages/SubAdmins/SubAdmins";
import ProductDetails from "./Admin/Pages/Products/ProductDetails";
import { registerServiceWorker } from "./registerServiceWorker";
import { requestAndGetToken } from "./firebase/fcm";
import { listenForMessages } from "./firebase/notificationListener";
import FCMTokenButton from "./Components/FCMTokenButton";
import ClientDetails from "./Admin/Pages/ClientsDistributors/ClientDetails";
import ProfileProgram from "./Admin/Pages/ProfileProgram/ProfileProgram";
import CustomerDetails from "./Admin/Pages/Quotations/Components/CustomerDetails";
import QuotationDetails from "./Admin/Pages/Quotations/QuotationDetails/QuotationDetails";
import ClientQuotationApprovePage from "./Admin/Pages/Quotations/QuotationDetails/ClientQuotationApprovePage";
import QuoteApproveSuccessPage from "./Admin/Pages/Quotations/QuotationDetails/QuoteApproveSuccessPage";
import OrderDetails from "./Admin/Pages/Orders/OrderDetails";
import DeepLinkingHandler from "./helpers/DeepLinkingHandler";
import { useAttendanceSync } from "./helpers/useAttendanceSync";
import NotificationSettings from "./Admin/Pages/NotificationSettings/NotificationSettings";
import useAppUpdate from "./helpers/useAppUpdate";
import {
  useApkUpdater,
  useCapacitorUpdater,
} from "capacitor-3rddigital-appupdate";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import AppUpdateModal from "./Components/AppUpdateModal";
import FullScreenProgress from "./Components/FullScreenProgress";
import {
  isTimeVariant,
  useAppPackages,
  useIsTimeVariant,
} from "./helpers/appPackage";
import { NoInternetDrawer } from "./Components/NoInternetDrawer";
import { GlobalPullToRefresh } from "./Components/PullToRefresh";
import { SocketProvider } from "./contexts/SocketContext";
import NetworkRefreshModal from "./Components/NetworkRefreshModal";
import UpdatePriceSheetVariables from "./Admin/Pages/PriceSheet/UpdatePriceSheetVariables";
import ImportVariants from "./Admin/Pages/Products/ImportVariants";
import CreatePriceSheet from "./Admin/Pages/PriceSheet/CreatePriceSheet";
import PrintJobLabel from "./Admin/Pages/Orders/PrintJobLabel";
import LazyWithRetry from "./helpers/LazyWithRetry";
import DeliveryLocations from "./Admin/Pages/MasterData/DeliveryLocations";
import ProductTypes from "./Admin/Pages/MasterData/ProductTypes";
import StorageTypes from "./Admin/Pages/MasterData/StorageTypes";
import ProductTags from "./Admin/Pages/MasterData/ProductTags";
import SummaryDetails from "./Admin/Pages/MasterData/SummaryDetails";
import DeliveryPricing from "./Admin/Pages/MasterData/DeliveryPricing/DeliveryPricing";
import CreateZone from "./Admin/Pages/MasterData/DeliveryPricing/CreateZone";
import { VersionPoller } from "./helpers/useVersionPolling";
import Colors from "./Admin/Pages/MasterData/Colors/Colors";
import MachineDetails from "./Admin/Pages/MasterData/MachineDetails";
import BOMSearch from "./Admin/Pages/MasterData/BOMSearch";
import CreatedLoadNotice from "./Admin/Pages/CreatedLoadNotice/CreatedLoadNotice";
import { PagePermissionNameData } from "./helpers/permissionModule";
import ClockInHistoryWrapper from "./Admin/Pages/ClockInHistory/ClockInHistoryWrapper";
import Calendar from "./Admin/Pages/ClockInHistory/Calendar";

// estimation pages
const Estimations = LazyWithRetry(
  () => import("./Admin/Pages/Estimations/Estimations"),
);
const CreateEstimation = LazyWithRetry(
  () => import("./Admin/Pages/Estimations/components/CreateEstimation"),
);
const EstimationDetails = LazyWithRetry(
  () => import("./Admin/Pages/Estimations/EstimationDetails/EstimationDetails"),
);
const ClientEstimationApprovePage = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/Estimations/EstimationDetails/ClientEstimationApprovePage"
    ),
);
const ClientSurveyDetailsPage = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/Estimations/EstimationDetails/ClientSurveyDetailsPage"
    ),
);
const EstimationAuditLog = LazyWithRetry(
  () => import("./Admin/Pages/Estimations/EstimationAuditLog"),
);

// auth page
const Login = LazyWithRetry(() => import("./Auth/Pages/Login"));
const AuthIndex = LazyWithRetry(() => import("./Auth/AuthIndex"));

const ForgotPassword = LazyWithRetry(
  () => import("./Auth/Pages/ForgotPassword"),
);
const ResetPassword = LazyWithRetry(() => import("./Auth/Pages/Resetpassword"));
const PageNotFound = LazyWithRetry(() => import("./Components/Error"));
const Profile = LazyWithRetry(() => import("./Admin/Pages/Profile"));
const FaceClockInOut = LazyWithRetry(
  () => import("./Components/FaceClockInOut"),
);

// admin pages
const AdminIndex = LazyWithRetry(() => import("./Admin/AdminIndex"));
const ChangePassword = LazyWithRetry(
  () => import("./Admin/Pages/ChangePassword"),
);
const DownloadApp = LazyWithRetry(
  () => import("./Admin/Pages/DownloadApp/DownloadApp"),
);
const Dashboard = LazyWithRetry(
  () => import("./Admin/Pages/Dashboard/Dashboard"),
);
const FinanceDashboard = LazyWithRetry(
  () => import("./Admin/Pages/Dashboard/FinanceDashboard"),
);
const EmployeeAttendance = LazyWithRetry(
  () => import("./Admin/Pages/Dashboard/EmployeeAttendance"),
);
const PendingLogs = LazyWithRetry(
  () => import("./Admin/Pages/Dashboard/PendingLogs"),
);
const OvertimeApproval = LazyWithRetry(
  () => import("./Admin/Pages/OvertimeApproval/OvertimeApproval"),
);
const HRManagerDashboard = LazyWithRetry(
  () => import("./Admin/Pages/Dashboard/HRManagerDashboard"),
);
const ClockInHistory = LazyWithRetry(
  () => import("./Admin/Pages/ClockInHistory/ClockInHistory"),
);
const ClockInTimeOffHistory = LazyWithRetry(
  () => import("./Admin/Pages/ClockInHistory/ClockInTimeOffHistory"),
);
const Allowance = LazyWithRetry(
  () => import("./Admin/Pages/ClockInHistory/Allowance"),
);
const TimeOffList = LazyWithRetry(
  () => import("./Admin/Pages/TimeOffList/TimeOffList"),
);
const EmployeeProfileDetails = lazy(
  () => import("./Admin/Pages/TeamMembers/EmployeeProfileDetails"),
);
const EmployeeProfileHistory = lazy(
  () => import("./Admin/Pages/TeamMembers/EmployeeProfileHistory"),
);
const PtoHistory = lazy(() => import("./Admin/Pages/TeamMembers/PtoHistory"));
const BookTimeOffHistory = lazy(
  () => import("./Admin/Pages/TeamMembers/BookTimeOffHistory"),
);
const TodayAvailability = lazy(
  () => import("./Admin/Pages/TeamMembers/TodayAvailability"),
);

const Projects = LazyWithRetry(() => import("./Admin/Pages/Projects/Projects"));
const ProjectDetails = LazyWithRetry(
  () => import("./Admin/Pages/Projects/ProjectDetails"),
);

const Quotations = LazyWithRetry(
  () => import("./Admin/Pages/Quotations/Quotations"),
);
const QuoteRequests = LazyWithRetry(
  () => import("./Admin/Pages/QuoteRequests/QuoteRequests"),
);
const QuoteRequestDetail = LazyWithRetry(
  () => import("./Admin/Pages/QuoteRequests/QuoteRequestDetail"),
);
const PublicQuoteRequest = LazyWithRetry(
  () => import("./Admin/Pages/QuoteRequests/PublicQuoteRequest"),
);
const QuoteRequestAuditLogs = LazyWithRetry(
  () => import("./Admin/Pages/QuoteRequests/QuoteRequestAuditLogs"),
);
const QuoteRequestStatusTimeline = LazyWithRetry(
  () => import("./Admin/Pages/QuoteRequests/QuoteRequestStatusTimeline"),
);
const QuotationPdfDownload = LazyWithRetry(
  () => import("./Admin/Pages/Quotations/QuotationPdfDownload"),
);
const CreateQuotations = LazyWithRetry(
  () => import("./Admin/Pages/Quotations/CreateQuotations"),
);
const CreateQuoteRequest = LazyWithRetry(
  () => import("./Admin/Pages/QuoteRequests/CreateQuoteRequest"),
);

const Orders = LazyWithRetry(() => import("./Admin/Pages/Orders/Orders"));
const DepartmentProductionPlanPage = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/ProductionPlan/Components/DepartmentProductionPlanPage"
    ),
);
const ProductionPlan = LazyWithRetry(
  () => import("./Admin/Pages/ProductionPlan/ProductionPlan"),
);
const InstallationPlan = LazyWithRetry(
  () => import("./Admin/Pages/InstallationPlan/InstallationPlan"),
);
const ViewInstallationDetails = LazyWithRetry(
  () => import("./Admin/Pages/InstallationPlan/ViewInstallationDetails"),
);

const ModifyInstallationPlan = LazyWithRetry(
  () => import("./Admin/Pages/InstallationPlan/ModifyInstallationPlan"),
);

const StockList = LazyWithRetry(
  () => import("./Admin/Pages/StockManager/StockList"),
);
const CreateStock = LazyWithRetry(
  () => import("./Admin/Pages/StockManager/CreateStock"),
);
const ViewStock = LazyWithRetry(
  () => import("./Admin/Pages/StockManager/ViewStock"),
);
const PurchaseOrders = LazyWithRetry(
  () => import("./Admin/Pages/StockManager/PurchaseOrders"),
);
const PoDetails = LazyWithRetry(
  () => import("./Admin/Pages/StockManager/PoDetails"),
);
const Suppliers = LazyWithRetry(
  () => import("./Admin/Pages/StockManager/Suppliers"),
);

const Products = LazyWithRetry(() => import("./Admin/Pages/Products/Products"));
const CreateProduct = LazyWithRetry(
  () => import("./Admin/Pages/Products/CreateProduct"),
);
const ViewProduct = LazyWithRetry(
  () => import("./Admin/Pages/Products/ViewProduct"),
);

const StaticData = LazyWithRetry(
  () => import("./Admin/Pages/StaticData/StaticData"),
);

const PriceSheet = LazyWithRetry(
  () => import("./Admin/Pages/PriceSheet/PriceSheet"),
);
const ImportPriceSheet = LazyWithRetry(
  () => import("./Admin/Pages/PriceSheet/ImportPriceSheet"),
);
const PriceSheetDetails = LazyWithRetry(
  () => import("./Admin/Pages/PriceSheet/PriceSheetDetails"),
);
const PriceSheetProductDetails = LazyWithRetry(
  () => import("./Admin/Pages/PriceSheet/PriceSheetProductDetails"),
);

const InstallationChecklistQuestions = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/InstallationChecklistQuestions"),
);
const MachinesAndDepartments = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/MachinesAndDepartments"),
);

const Settings = LazyWithRetry(() => import("./Admin/Pages/Settings/Settings"));
const SettingsChangeHistory = LazyWithRetry(
  () => import("./Admin/Pages/Settings/SettingsChangeHistory"),
);
const RestartAppHistory = LazyWithRetry(
  () => import('./Admin/Pages/Settings/RestartAppHistoryPage')
);

const RolesAndPermissions = LazyWithRetry(
  () => import("./Admin/Pages/RolesAndPermissions/RolesAndPermissions"),
);
const EditRolesAndPermissions = LazyWithRetry(
  () => import("./Admin/Pages/RolesAndPermissions/EditRolesAndPermissions"),
);

const NotificationEmailTemplates = LazyWithRetry(
  () =>
    import("./Admin/Pages/NotificationEmailTemplates/NotificationTemplates"),
);
const EditNotificationEmailTemplates = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/NotificationEmailTemplates/EditNotificationEmailTemplates"
    ),
);

const ViewNotificationEmailTemplateDetails = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/NotificationEmailTemplates/ViewNotificationEmailTemplateDetails"
    ),
);

const ClientsDistributors = LazyWithRetry(
  () => import("./Admin/Pages/ClientsDistributors/ClientsDistributors"),
);
const TeamMembers = LazyWithRetry(
  () => import("./Admin/Pages/TeamMembers/TeamMembers"),
);
const TeamMemberDetails = LazyWithRetry(
  () => import("./Admin/Pages/TeamMembers/TeamMemberDetails"),
);

const SalesCommission = LazyWithRetry(
  () => import("./Admin/Pages/SalesCommission/SalesCommission"),
);
const SalesCommissionDetails = LazyWithRetry(
  () => import("./Admin/Pages/SalesCommission/SalesCommissionDetails"),
);

const Services = LazyWithRetry(() => import("./Admin/Pages/Services/Services"));

const PalletCalculator = LazyWithRetry(
  () => import("./Admin/Pages/PalletCalculator/PalletCalculator"),
);
const StaticPages = LazyWithRetry(
  () => import("./Admin/Pages/StaticPages/StaticPages"),
);
const EditStaticPage = LazyWithRetry(
  () => import("./Admin/Pages/StaticPages/EditStaticPage"),
);
const ViewStaticPage = LazyWithRetry(
  () => import("./Admin/Pages/StaticPages/ViewStaticPage"),
);
const ClientInstallatiopnReviewPage = LazyWithRetry(
  () => import("./Admin/Pages/Orders/ClientInstallatiopnReviewPage"),
);

const QuoteAuditLog = LazyWithRetry(
  () => import("./Admin/Pages/Quotations/QuoteAuditLog"),
);
const Manufacturing = LazyWithRetry(
  () => import("./Admin/Pages/Manufacturing/Manufacturing"),
);
const ManufacturingDetails = LazyWithRetry(
  () => import("./Admin/Pages/Manufacturing/ManufacturingDetails"),
);
const Department = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/Department"),
);
const HiredCompany = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/HiredCompany"),
);
const QuoteAdditionalInfo = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/QuoteAdditionalInfo"),
);
const PricesheetVariablesInfo = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/PricesheetVariablesInfo"),
);
const ServerQueueList = LazyWithRetry(
  () => import("./Admin/Pages/ServerQueueList/ServerQueueList"),
);
const SlowApiLogs = LazyWithRetry(
  () => import("./Admin/Pages/SlowApiLogs/SlowApiLogs"),
);

const PricesheetHistory = LazyWithRetry(
  () => import("./Admin/Pages/PriceSheet/PricesheetHistory"),
);

const Invoice = LazyWithRetry(() => import("./Admin/Pages/Invoice/Invoice"));
const CreateInvoice = LazyWithRetry(
  () => import("./Admin/Pages/Invoice/CreateInvoice"),
);
const EditInvoice = LazyWithRetry(
  () => import("./Admin/Pages/Invoice/EditInvoice"),
);
const InvoiceDetails = LazyWithRetry(
  () => import("./Admin/Pages/Invoice/InvoiceDetails"),
);
const CustomerInvoiceDetails = LazyWithRetry(
  () => import("./Admin/Pages/Invoice/CustomerInvoiceDetails"),
);

const SageCallback = LazyWithRetry(
  () => import("./Admin/Pages/Sage/SageCallback"),
);
const TestSageData = LazyWithRetry(
  () => import("./Admin/Pages/Sage/TestSageData"),
);

// Stock Process Manger
const SPMProcesses = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/Processes/Processes"),
);
const SPMCreateProcesses = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/Processes/CreateProcesses"),
);
const SPMComponents = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/Components/Components"),
);
const SPMCreateComponents = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/Components/CreateComponents"),
);
const SPMParts = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/Parts/Parts"),
);
const SPMCreateParts = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/Parts/CreateParts"),
);
const SPMProductBOM = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/ProductBOM/ProductBOM"),
);
const SPMCreateProductBOM = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/ProductBOM/CreateProductBOM"),
);
const SPMViewProductBOM = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/ProductBOM/ViewProductBOM"),
);
const SPMImportBOM = LazyWithRetry(
  () => import("./Admin/Pages/StockProcessManager/ProductBOM/ImportBOM"),
);

const QualityChecklist = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/QualityChecklist"),
);

const QualityCheckQuestionsList = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/QualityCheckQuestionsList"),
);
const MezzanineFloorSettings = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/MezzanineFloorSettings"),
);
const ReadinessCheck = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/ReadinessCheck"),
);
const ReadinessCheckDetails = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/ReadinessCheckDetails"),
);
const AdjustablePalletRacking = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/ManageLoadNotice/AdjustablePalletRacking/AdjustablePalletRacking"
    ),
);
const CreateAdjustablePalletRacking = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/ManageLoadNotice/AdjustablePalletRacking/CreateAdjustablePalletRacking"
    ),
);
const APRLoadNoticeCreator = LazyWithRetry(
  () => import("./Admin/Pages/LoadNoticeCreator/APRLoadNoticeCreator"),
);

const ViewAdjustablePalletRacking = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/ManageLoadNotice/AdjustablePalletRacking/ViewAdjustablePalletRacking"
    ),
);

const SteelCoilManager = LazyWithRetry(
  () => import("./Admin/Pages/SteelCoilManager/SteelCoilManager"),
);
const SteelCoilDetail = LazyWithRetry(
  () => import("./Admin/Pages/SteelCoilManager/SteelCoilDetail"),
);

const DispatchChecklist = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/DispatchChecklist"),
);

const DispatchChecklistQuestions = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/DispatchChecklistQuestions"),
);

const BespokeItems = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/BespokeItems"),
);

const ParcelBoxes = LazyWithRetry(
  () => import("./Admin/Pages/MasterData/ParcelBoxes"),
);

const QuotationEmailFormat = LazyWithRetry(
  () =>
    import(
      "./Admin/Pages/Quotations/QuotationEmailFormat/QuotationEmailFormat"
    ),
);

const HRSettings = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/HRSettings"),
);
const ManageTimeOff = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/ManageTimeOff"),
);
const AutoClockOut = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/AutoClockOut"),
);
const Payroll = LazyWithRetry(() => import("./Admin/Pages/HRSettings/Payroll"));
const HRDashboardClockedHours = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/HRDashboardClockedHours"),
);
const ManageHolidays = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/ManageHolidays"),
);
const ManageAllowance = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/ManageAllowance"),
);
const PayrollRules = LazyWithRetry(
  () => import("./Admin/Pages/HRSettings/PayrollRules"),
);
const EmployeeNotes = LazyWithRetry(
  () => import("./Admin/Pages/EmployeeNotes/EmployeeNotes"),
);
const Events = LazyWithRetry(() => import("./Admin/Pages/HRSettings/Events"));

const LeanProductInventory = LazyWithRetry(
  () => import("./Admin/Pages/LeanProductInventory/LeanProductInventory"),
);
const InventoryCheckInOutHistory = LazyWithRetry(
  () => import("./Admin/Pages/LeanProductInventory/InventoryCheckInOutHistory"),
);
const EccomerceCategory = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EccomerceCategory"),
);
const EccomerceProduct = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EccomerceProduct"),
);
const EditEcommerceProduct = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EditEcommerceProduct"),
);
const ViewEcommerceProduct = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/ViewEcommerceProduct"),
);
const EccomerceSeo = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EccomerceSeo"),
);
const CreateOrEditEcommerceSeo = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/CreateOrEditEcommerceSeo"),
);
const ViewEcommerceSeo = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/ViewEcommerceSeo"),
);
const EccomerceBanners = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EccomerceBanners"),
);
const CreateOrEditEcommerceBanner = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/CreateOrEditEcommerceBanner"),
);
const EccomerceWhyChooseUs = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EccomerceWhyChooseUs"),
);
const EccomerceContact = LazyWithRetry(
  () => import("./Admin/Pages/EcommerceSite/EccomerceContact"),
);



const customLocale: any = {
  ...enUS,
  DatePicker: {
    ...enUS.DatePicker,
    lang: {
      ...enUS.DatePicker?.lang,
      firstDayOfWeek: 1,
    },
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

function AttendanceSyncHandler() {
  useAttendanceSync();
  return null;
}

/**
 * Arms the native auto start / auto close schedule (attendance app). All time
 * logic lives in helpers/appLifecycleScheduler — the native side only executes
 * the computed instants.
 */
function AppLifecycleSchedulerHandler() {
  useEffect(() => initAppLifecycleScheduler(), []);
  return null;
}

/**
 * Reports the attendance tablet's app open / close moments to the backend
 * (`/device-tracking`) so the auto start / auto close feature can be verified
 * from the admin "Restart App History" screen. Start is also reported the moment
 * the tablet user logs in (the store gains a user).
 */
function DeviceTrackingHandler() {
  const { user } = useAppSelector((state: RootState) => state.auth);

  useEffect(() => initDeviceTracking(), []);

  useEffect(() => {
    if (user) void trackDeviceStart();
  }, [user]);

  return null;
}

function RouteTracker() {
  const location = useLocation();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const userId = user?._id || getLoggedInUserId();

  useEffect(() => {
    // Check if there is a pending status lock toast from a redirect
    const lockToast = sessionStorage.getItem("status_lock_redirect_toast");
    if (lockToast) {
      sessionStorage.removeItem("status_lock_redirect_toast");
      try {
        const parsed = JSON.parse(lockToast);
        message.info({
          content: (
            <span>
              This {parsed.entity || "record"} has already been converted to{" "}
              <strong>{parsed.status || "Quote Created"}</strong>. So, cannot be
              edited.
            </span>
          ),
          key: "status_lock_redirect_toast",
        });
      } catch (e) {
        message.info({
          content: lockToast,
          key: "status_lock_redirect_toast",
        });
      }
    }

    console.log(
      "RouteTracker triggered! pathname:",
      location.pathname,
      "userId:",
      userId,
    );

    // Fallback if userId is not ready yet but we have it in localStorage
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      try {
        const lastActiveAccountStr = localStorage.getItem(
          "last_active_account",
        );
        if (lastActiveAccountStr) {
          const lastActiveAccount = JSON.parse(lastActiveAccountStr);
          effectiveUserId = lastActiveAccount?.account_id;
        }
      } catch (e) {
        // ignore
      }
    }

    if (effectiveUserId) {
      const excludedPaths = ["/login", "/forgot-password", "/face-scanner"];
      if (
        !excludedPaths.some((path) => location.pathname.startsWith(path)) &&
        location.pathname !== "/"
      ) {
        const fullPath = location.pathname + location.search;
        try {
          localStorage.setItem(`lastVisitedPage_${effectiveUserId}`, fullPath);
          console.log("Successfully saved lastVisitedPage to:", fullPath);
        } catch (error) {
          console.error("Failed to save last visited page:", error);
        }
      } else {
        console.log("Path excluded or is root. Path:", location.pathname);
      }
    } else {
      console.log("userId is null! Cannot save lastVisitedPage.");
    }

    // Capacitor specific listener to save aggressively when app goes to background
    let listener: any = null;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (!isActive && effectiveUserId) {
          const currentPath = window.location.pathname;
          const excludedPaths = ["/login", "/forgot-password", "/face-scanner"];
          if (
            !excludedPaths.some((path) => currentPath.startsWith(path)) &&
            currentPath !== "/"
          ) {
            const fullPath = currentPath + window.location.search;
            try {
              localStorage.setItem(
                `lastVisitedPage_${effectiveUserId}`,
                fullPath,
              );
            } catch (e) { }
          }
        }
      }).then((l) => {
        listener = l;
      });
    }

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [location.pathname, location.search, userId]);

  return null;
}

const AttendanceTabletGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const tokenData = getDecryptedUserData();
      const roleKey = tokenData?.role?.key || "";
      const roleName = String(tokenData?.role?.name || "").toLowerCase();
      const isAttendanceTabletRole =
        roleKey === ROLE.ATTENDANCE_TABLET ||
        roleKey === "attendance-tablet" ||
        roleName.includes("attendance tablet") ||
        roleName.includes("attendance-tablet");

      if (tokenData && isAttendanceTabletRole) {
        if (
          location.pathname !== "/face-scanner" &&
          location.pathname !== "/local-debug" &&
          location.pathname !== "/download-app"
        ) {
          navigate("/face-scanner", { replace: true });
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [location.pathname, navigate]);

  return null;
};

const TimeVariantGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isTime = useIsTimeVariant();

  useEffect(() => {
    const checkTimeVariant = async () => {
      const isTimeApp = isTime || (await isTimeVariant());
      if (!isTimeApp) return;

      const tokenData = getDecryptedUserData();
      const token = getDecryptedToken();
      if (!token || !tokenData) return;

      const roleKey = tokenData?.role?.key || "";
      const roleName = String(tokenData?.role?.name || "").toLowerCase();
      const isAttendanceTabletRole =
        roleKey === ROLE.ATTENDANCE_TABLET ||
        roleKey === "attendance-tablet" ||
        roleName.includes("attendance tablet") ||
        roleName.includes("attendance-tablet");

      if (isAttendanceTabletRole) return;

      const allowedPrefixes = [
        "/clock-in-history",
        "/profile",
        "/change-password",
        "/download-app",
        "/local-debug",
        "/calendar",
      ];

      const currentPath = location.pathname;
      const isAllowed = allowedPrefixes.some(
        (prefix) =>
          currentPath === prefix || currentPath.startsWith(prefix + "/"),
      );

      if (!isAllowed) {
        navigate("/clock-in-history", { replace: true });
      }
    };

    checkTimeVariant();
  }, [location.pathname, isTime, navigate]);

  return null;
};

function App() {
  const { getUpdateAvailable } = useAppUpdate();
  const [updateProgress, setUpdateProgress] = useState(0);
  const [appUpdateModal, setAppUpdateModal] = useState(false);
  const appPackages = useAppPackages();

  const user = getDecryptedUserData();

  useEffect(() => {
    if (user) {
      setExceptionUserInfo({ id: user?._id, email: user?.email });
      if (Capacitor.isNativePlatform()) {
        NativeExceptionHandler.setContext({
          userInfo: {
            id: user?._id,
            email: user?.email,
          },
        });
      }
      return;
    }
    clearExceptionUserInfo(["id", "email"]);
    if (Capacitor.isNativePlatform()) {
      NativeExceptionHandler.setContext({
        userInfo: {},
      });
    }
  }, [user]);

  const {
    apkUpdateInfo,
    isApkUpdateModalVisible,
    setApkUpdateModalVisible,
    handleApkUpdate,
    apkProgress,
    apkProgressInfo,
    apkPhase,
    isApkUpdating,
    ensureApkPermissions,
    apkUpdatePriority,
  } = useApkUpdater({
    baseUrl: process.env.REACT_APP_APPUPDATE_BASE_URL ?? "",
    apiKey: process.env.REACT_APP_APPUPDATE_API_KEY,
    projectKey: process.env.REACT_APP_UPDATE_KEY,
    packageName: appPackages?.androidPackage,
    showProgress: true,
    onUpdateSuccess: () => {
      message.success("App updated successfully");
    },
  });

  const {
    isUpdateModalVisible,
    updateInfo,
    handleUpdate,
    setUpdateModalVisible,
  } = useCapacitorUpdater({
    baseUrl: process.env.REACT_APP_APPUPDATE_BASE_URL ?? "",
    apiKey: process.env.REACT_APP_APPUPDATE_API_KEY,
    projectKey: process.env.REACT_APP_UPDATE_KEY,
    iosPackage: appPackages?.iosPackage,
    androidPackage: appPackages?.androidPackage,
    showProgress: true,
    onProgress: (p) => {
      setUpdateProgress(p);
    },
    apkUpdatePriority: apkUpdatePriority,
  });

  const isApkUpdateFlowActive = isApkUpdateModalVisible;
  const apkHasPriority =
    apkUpdatePriority === "checking" || apkUpdatePriority === "blocked";
  const isStoreUpdateFlowActive =
    appUpdateModal &&
    !isApkUpdateModalVisible &&
    !isApkUpdating &&
    !apkHasPriority;
  const isInAppUpdateFlowActive =
    isUpdateModalVisible &&
    !isApkUpdateModalVisible &&
    !isApkUpdating &&
    !apkHasPriority;
  const isAnyUpdateModalVisible =
    isStoreUpdateFlowActive || isInAppUpdateFlowActive || isApkUpdateFlowActive;

  const handleCloseAppUpdateModal = () => {
    setAppUpdateModal(false);
    setUpdateModalVisible(false);
    setApkUpdateModalVisible(false);
  };

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    initializeCapacitor();
    ensureApkPermissions();
    const appUpdateCheck = async () => {
      try {
        const updateAvailable = await getUpdateAvailable();
        if (updateAvailable) {
          setAppUpdateModal(true);
        }
      } catch (e) { }
    };

    appUpdateCheck();

    const wasUpdating = localStorage.getItem("UPDATE_IN_PROGRESS");
    if (wasUpdating === "true") {
      message.success("App updated successfully");
      localStorage.removeItem("UPDATE_IN_PROGRESS");
    }
  }, []);

  // useEffect(() => {
  //   const init = async () => {
  //     try {
  //       // Skip service worker in development
  //       if (process.env.NODE_ENV !== 'development') {
  //         await registerServiceWorker();
  //       }

  //       // Only request FCM token if user is logged in
  //       const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
  //       if (authToken) {
  //         console.log("🔔 User is logged in, requesting FCM token...");
  //         await requestAndGetToken();
  //       } else {
  //         console.log("ℹ️ User not logged in, skipping FCM token request");
  //       }

  //       listenForMessages();
  //     } catch (error) {
  //       console.error("❌ Error initializing FCM:", error);
  //     }
  //   };

  //   init();
  // }, []);

  return (
    <ConfigProvider
      locale={customLocale}
      theme={{
        token: {
          fontFamily: "Nunito Sans Regular",
          colorPrimary: "#231F20",
        },
      }}
    >
      <AppWrapper>
        <GlobalPullToRefresh />
        <NoInternetDrawer />
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <BrowserRouter basename={alias}>
              <RouteTracker />
              <AppLifecycleSchedulerHandler />
              <DeviceTrackingHandler />
              <VersionPoller />
              {/* <BrowserRouter basename={alias}> */}
              <PersistGate loading={<SiteLoader />} persistor={persistor}>
                {/* FCM Test Button - Remove after testing */}
                {/* {process.env.NODE_ENV === 'development' && (
                <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 9999 }}>
                  <FCMTokenButton />
                </div>
              )} */}

                <Suspense fallback={<SiteLoader />}>
                  <ExceptionBoundary FallbackComponent={ErrorFallbackPage}>
                    <SocketProvider>

                      <AttendanceTabletGuard />
                      <TimeVariantGuard />
                      <Routes>
                        {/* Auth */}
                        {/* <Route path="/" element={<Home />} /> */}
                        <Route path="p/:url" element={<ViewStaticPage />} />
                        {/* <Route
                          path="/quotation-details/:id"
                          element={<ClientQuotationApprovePage />}
                        /> */}
                        <Route
                          path="/estimate-details/:id"
                          element={<ClientEstimationApprovePage />}
                        />
                        <Route
                          path="/survey-details/:id"
                          element={<ClientSurveyDetailsPage />}
                        />
                        {/* <Route
                          path="/quotation-approve-success"
                          element={
                            <QuoteApproveSuccessPage
                              quote_data={null}
                              setViewMode={() => { }}
                            />
                          }
                        /> */}
                        <Route
                          path="/public/quote-request/:id"
                          element={<PublicQuoteRequest />}
                        />
                        <Route
                          path="purchase-orders/details/:id"
                          element={<PoDetails />}
                        />
                        <Route
                          path="/pay-invoice/:id"
                          element={<CustomerInvoiceDetails />}
                        />

                        <Route
                          path="/installation-confirmation/:id"
                          element={<ClientInstallatiopnReviewPage />}
                        />
                        <Route
                          path="/quotation-pdf/:id"
                          element={<QuotationPdfDownload />}
                        />
                        <Route element={<AuthIndex />}>
                          <Route
                            index
                            element={<Navigate to="login" replace />}
                          />
                          <Route path="login" element={<Login />} />
                          <Route
                            path="forgot-password"
                            element={<ForgotPassword />}
                          />
                          <Route
                            path="forgot-password/:token"
                            element={<ResetPassword />}
                          />
                        </Route>
                        {/*Admin  */}
                        <Route element={<AdminIndex notRequired={true} />}>
                          <Route
                            path="/face-scanner"
                            element={<FaceClockInOut />}
                          />
                          <Route path="/local-debug" element={<LocalDebug />} />
                          {/* <Route path="test" element={<CustomerDetails />} /> */}
                          <Route path="profile" element={<Profile />} />

                          <Route
                            path="change-password"
                            element={<ChangePassword />}
                          />

                          <Route
                            path="notification-settings"
                            element={<NotificationSettings />}
                          />

                          <Route
                            path="download-app"
                            element={<DownloadApp />}
                          />
                          <Route
                            path="slow-api-logs"
                            element={<SlowApiLogs />}
                          />
                        </Route>

                        <Route element={<AdminIndex moduleName="dashboard" />}>
                          <Route path="dashboard" element={<Dashboard />} />
                          <Route
                            path="dashboard/finance-dashboard"
                            element={<FinanceDashboard />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="projects" />}>
                          <Route path="projects" element={<Projects />} />
                          <Route
                            path="projects/:project_no"
                            element={<ProjectDetails />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="quote_requests" />}
                        >
                          <Route
                            path="quote-requests"
                            element={<QuoteRequests />}
                          />
                          <Route
                            path="quote-requests/:id"
                            element={<QuoteRequestDetail />}
                          />
                          <Route
                            path="quote-requests/create"
                            element={<CreateQuoteRequest />}
                          />
                          <Route
                            path="quote-requests/edit/:id"
                            element={<CreateQuoteRequest />}
                          />
                          <Route
                            path="quote-requests/audit-logs/:id"
                            element={<QuoteRequestAuditLogs />}
                          />
                          <Route
                            path="quote-requests/status-timeline/:id"
                            element={<QuoteRequestStatusTimeline />}
                          />
                        </Route>

                        <Route element={<AdminIndex moduleName="estimate" />}>
                          <Route
                            path="projects/:project_no"
                            element={<ProjectDetails />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="quote_requests" />}
                        >
                          <Route
                            path="quote-requests"
                            element={<QuoteRequests />}
                          />
                          <Route
                            path="quote-requests/:id"
                            element={<QuoteRequestDetail />}
                          />
                          <Route
                            path="quote-requests/create"
                            element={<CreateQuoteRequest />}
                          />
                          <Route
                            path="quote-requests/audit-logs/:id"
                            element={<QuoteRequestAuditLogs />}
                          />
                          <Route
                            path="quote-requests/status-timeline/:id"
                            element={<QuoteRequestStatusTimeline />}
                          />
                        </Route>

                        <Route element={<AdminIndex moduleName="estimate" />}>
                          <Route path="estimates" element={<Estimations />} />
                          <Route
                            path="estimates/create-estimate"
                            element={<CreateEstimation />}
                          />
                          <Route
                            path="estimates/:id"
                            element={<EstimationDetails />}
                          />
                          <Route
                            path="estimates/:id/audit-log"
                            element={<EstimationAuditLog />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="quotations" />}>
                          <Route path="quotations" element={<Quotations />} />
                          <Route
                            path="quotations/archived"
                            element={<Quotations />}
                          />
                          <Route
                            path="/quotations/create-quotation"
                            element={<CreateQuotations />}
                          />
                          <Route
                            path="/quotations/:id"
                            element={<QuotationDetails />}
                          />
                          <Route
                            path="/quotations/:id/audit-log"
                            element={<QuoteAuditLog audit_type="quote" />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="pallet_calculator" />
                          }
                        >
                          <Route
                            path="pallet-calculator"
                            element={<PalletCalculator />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="profile_program" />}
                        >
                          <Route
                            path="profile-program"
                            element={<ProfileProgram />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="orders" />}>
                          <Route path="orders/:id" element={<OrderDetails />} />
                          <Route path="orders" element={<Orders />} />
                          <Route path="orders/archived" element={<Orders />} />
                          <Route
                            path="orders/:id/audit-log"
                            element={<QuoteAuditLog audit_type="order" />}
                          />
                          <Route
                            path="order/print-label/:id/:cat_snap_id"
                            element={<PrintJobLabel />}
                          />
                        </Route>
                        <Route
                          element={
                            <FeatureGuard isEnabled={isInvoiceFeatureEnabled}>
                              <AdminIndex moduleName="invoice" />
                            </FeatureGuard>
                          }
                        >
                          <Route path="invoices" element={<Invoice />} />
                          <Route
                            path="invoices/create-invoice/:orderId/:milestoneId"
                            element={<CreateInvoice />}
                          />
                          <Route
                            path="invoices/edit-invoice/:id"
                            element={<EditInvoice />}
                          />
                          <Route
                            path="invoices/invoice-details/:id"
                            element={<InvoiceDetails />}
                          />
                        </Route>

                        <Route element={<AdminIndex moduleName="jobs" />}>
                          <Route
                            path="manufacturing"
                            element={<Manufacturing />}
                          />
                          <Route
                            path="manufacturing/:id/:splitsId"
                            element={<ManufacturingDetails />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="production_plan" />}
                        >
                          <Route
                            path="production-plan"
                            element={<DepartmentProductionPlanPage />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="installation_plan" />
                          }
                        >
                          <Route
                            path="installation-plan"
                            element={<InstallationPlan />}
                          />
                          <Route
                            path="installation-plan/view-detail/:id"
                            element={<ViewInstallationDetails />}
                          />
                          <Route
                            path="installation-plan/:id"
                            element={<ModifyInstallationPlan />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="sales_commission" />}
                        >
                          <Route
                            path="sales-commission"
                            element={<SalesCommission />}
                          />
                          <Route
                            path="sales-commission/:id"
                            element={<SalesCommissionDetails />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="clients_distributors" />
                          }
                        >
                          <Route
                            path="clients-distributors/:id"
                            element={<ClientDetails />}
                          />

                          <Route
                            path="clients-distributors"
                            element={<ClientsDistributors />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="team_members" />}
                        >
                          <Route
                            path="team-members"
                            element={<TeamMembers />}
                          />
                          <Route
                            path="team-members/employee-details/:id"
                            element={<EmployeeProfileDetails />}
                          />
                          <Route
                            path="team-members/employee-profile-history/:id"
                            element={<EmployeeProfileHistory />}
                          />
                          <Route
                            path="team-members/pto-history/:id"
                            element={<PtoHistory />}
                          />
                          <Route
                            path="team-members/employee-attendance/:id"
                            element={<EmployeeAttendance />}
                          />

                          <Route
                            path="team-members/time-off-history"
                            element={<BookTimeOffHistory />}
                          />
                          <Route
                            path="team-members/availability"
                            element={<TodayAvailability />}
                          />
                          <Route
                            path="team-members/time-off-history/:id"
                            element={<BookTimeOffHistory />}
                          />
                          <Route
                            path="team-members/employee-notes"
                            element={<EmployeeNotes />}
                          />
                          <Route
                            path="team-members/employee-notes/:id"
                            element={<EmployeeNotes />}
                          />
                          <Route
                            path="team-members/:id"
                            element={<TeamMemberDetails />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="employee_attendance" />
                          }
                        >
                          <Route
                            path="employee-attendance"
                            element={
                              <TodayAvailability
                                pagePermission={
                                  PagePermissionNameData?.EMPLOYEE_ATTENDANCE
                                }
                              />
                            }
                          />
                        </Route>
                        <Route element={<AdminIndex notRequired={true} />}>
                          <Route
                            path="clock-in-history"
                            element={<ClockInHistoryWrapper />}
                          />
                          <Route path="calendar" element={<Calendar />} />
                          <Route
                            path="clock-in-history/pto-history/:id"
                            element={<PtoHistory isEmbedded={true} />}
                          />
                          <Route path="time-off" element={<TimeOffList />} />
                          <Route
                            path="clock-in-history/pto-history/:id"
                            element={
                              <PtoHistory
                                pagePermission={
                                  PagePermissionNameData?.CLOCK_IN_HISTORY
                                }
                              />
                            }
                          />
                          {/* <Route
                            path="clock-in-history/allowance/:id"
                            element={<Allowance pagePermission={PagePermissionNameData?.CLOCK_IN_HISTORY} />}
                          /> */}
                        </Route>
                        <Route element={<AdminIndex notRequired={true} />}>
                          <Route
                            path="overtime-approval"
                            element={<OvertimeApproval />}
                          />
                          <Route path="time-off" element={<TimeOffList />} />
                          <Route
                            path="pending-logs"
                            element={<PendingLogs />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="price_sheet" />}
                        >
                          <Route path="price-sheet" element={<PriceSheet />} />
                          <Route
                            path="/price-sheet/update-sheet-variables/:id"
                            element={<UpdatePriceSheetVariables />}
                          />
                          <Route
                            path="/price-sheet/history/:id"
                            element={<PricesheetHistory />}
                          />
                          <Route
                            path="/price-sheet/create"
                            element={<CreatePriceSheet />}
                          />
                          <Route
                            path="/price-sheet/:id/edit"
                            element={<CreatePriceSheet />}
                          />
                          <Route
                            path="/price-sheet/:id/duplicate"
                            element={<CreatePriceSheet />}
                          />
                          <Route
                            path="/price-sheet/:id/import"
                            element={<ImportPriceSheet />}
                          />
                          <Route
                            path="/price-sheet/:id/details/:productId"
                            element={<PriceSheetProductDetails />}
                          />
                          <Route
                            path="/price-sheet/:id/details"
                            element={<PriceSheetDetails />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="products" />}>
                          <Route path="products" element={<Products />} />
                          <Route
                            path="products/details/:productId"
                            element={<ProductDetails />}
                          />
                          {/* <Route
                          path="products/:productTypeId"
                          element={<Products />}
                        /> */}
                          <Route
                            path="products/edit/:productId"
                            element={<CreateProduct />}
                          />
                          <Route
                            path="products/import-variants/:productId"
                            element={<ImportVariants />}
                          />
                          <Route
                            path="products/duplicate/:productId"
                            element={<CreateProduct />}
                          />
                          <Route
                            path="products/create"
                            element={<CreateProduct />}
                          />
                          <Route
                            path="products/view-product"
                            element={<ViewProduct />}
                          />
                          <Route
                            path="/product-details"
                            element={<PriceSheetProductDetails />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="off_shelf_products" />
                          }
                        >
                          <Route
                            path="off-self-products"
                            element={<LeanProductInventory />}
                          />
                          <Route
                            path="off-self-products/history/:id"
                            element={<InventoryCheckInOutHistory />}
                          />
                        </Route>
                         <Route
                          element={
                            <AdminIndex moduleName="ecommerce" notRequired={true}/>
                          }
                        >
                          <Route
                            path="ecommerce/categories"
                            element={<EccomerceCategory />}
                          />
                             <Route
                            path="ecommerce/products"
                            element={<EccomerceProduct />}
                          />
                          <Route
                            path="ecommerce/products/edit/:id"
                            element={<EditEcommerceProduct />}
                          />
                          <Route
                            path="ecommerce/products/view/:id"
                            element={<ViewEcommerceProduct />}
                          />
                          <Route
                            path="ecommerce/seo"
                            element={<EccomerceSeo />}
                          />
                          <Route
                            path="ecommerce/seo/create"
                            element={<CreateOrEditEcommerceSeo />}
                          />
                          <Route
                            path="ecommerce/seo/edit/:id"
                            element={<CreateOrEditEcommerceSeo />}
                          />
                          <Route
                            path="ecommerce/seo/view/:id"
                            element={<ViewEcommerceSeo />}
                          />
                          <Route
                            path="ecommerce/banners"
                            element={<EccomerceBanners />}
                          />
                          <Route
                            path="ecommerce/banners/create"
                            element={<CreateOrEditEcommerceBanner />}
                          />
                          <Route
                            path="ecommerce/banners/edit/:id"
                            element={<CreateOrEditEcommerceBanner />}
                          />
                          <Route
                            path="ecommerce/why-choose-us"
                            element={<EccomerceWhyChooseUs />}
                          />
                          <Route
                            path="ecommerce/contacts"
                            element={<EccomerceContact />}
                          />

                        </Route>
                        

                        <Route
                          element={
                            <AdminIndex moduleName="parts_restriction" />
                          }
                        >
                          <Route
                            path="parts-restriction"
                            element={<PartsRestriction />}
                          />

                          <Route
                            path="parts-restriction/import"
                            element={<ImportPartsRestrictions />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="created_load_notice" />
                          }
                        >
                          <Route
                            path="created-load-notice"
                            element={<CreatedLoadNotice />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="services" />}>
                          <Route path="services" element={<Services />} />
                        </Route>

                        <Route element={<AdminIndex moduleName="sub_admins" />}>
                          <Route path="sub-admins" element={<SubAdmins />} />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="quotation_email_format" />
                          }
                        >
                          <Route
                            path="quotation-email-format"
                            element={<QuotationEmailFormat />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="settings" />}>
                          <Route path="settings" element={<Settings />} />
                          <Route
                            path="settings/change-history"
                            element={<SettingsChangeHistory />}
                          />
                          <Route
                            path="settings/restart-app-history"
                            element={<RestartAppHistory />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="master_data" />}
                        >
                          <Route
                            path="master-data/bespoke-items"
                            element={<BespokeItems />}
                          />
                          <Route
                            path="master-data/installation-checklist"
                            element={<InstallationChecklistQuestions />}
                          />
                          <Route
                            path="master-data/machines-departments/:id/details"
                            element={<MachineDetails />}
                          />
                          <Route
                            path="master-data/machines-departments"
                            element={<MachinesAndDepartments />}
                          />

                          <Route
                            path="master-data/departments"
                            element={<Department />}
                          />
                          <Route
                            path="master-data/hired-companies"
                            element={<HiredCompany />}
                          />
                          <Route
                            path="master-data/quotation-additional-info"
                            element={<QuoteAdditionalInfo />}
                          />
                          <Route
                            path="master-data/pricesheet-variables-info"
                            element={<PricesheetVariablesInfo />}
                          />
                          {/* <Route
                            path="master-data/delivery-locations"
                            element={<DeliveryLocations />}
                          /> */}
                          <Route
                            path="master-data/product-types"
                            element={<ProductTypes />}
                          />
                          <Route
                            path="master-data/product-tags"
                            element={<ProductTags />}
                          />
                          <Route
                            path="master-data/summary-details"
                            element={<SummaryDetails />}
                          />
                          <Route
                            path="master-data/delivery-pricing"
                            element={<DeliveryPricing />}
                          />
                          <Route
                            path="master-data/powder-coating-colours"
                            element={<Colors />}
                          />
                          <Route
                            path="master-data/delivery-pricing/create-zone"
                            element={<CreateZone />}
                          />
                          <Route
                            path="master-data/delivery-pricing/create-zone/:_id"
                            element={<CreateZone />}
                          />
                          <Route
                            path="master-data/quality-check"
                            element={<QualityChecklist />}
                          />
                          <Route
                            path="master-data/quality-check/:id"
                            element={<QualityCheckQuestionsList />}
                          />
                          <Route
                            path="master-data/dispatch-checklist"
                            element={<DispatchChecklist />}
                          />
                          <Route
                            path="master-data/dispatch-checklist/:id"
                            element={<DispatchChecklistQuestions />}
                          />
                          <Route
                            path="master-data/mezzanine-floors-settings"
                            element={<MezzanineFloorSettings />}
                          />
                          <Route
                            path="master-data/storage-types"
                            element={<StorageTypes />}
                          />
                          <Route
                            path="master-data/readiness-check"
                            element={<ReadinessCheck />}
                          />
                          <Route
                            path="master-data/readiness-check/:id"
                            element={<ReadinessCheckDetails />}
                          />
                          <Route
                            path="master-data/parcel-boxes"
                            element={<ParcelBoxes />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="hr_settings" />}
                        >
                          <Route
                            path="hr-settings/settings"
                            element={<HRSettings />}
                          />
                          <Route
                            path="hr-settings/dashboard"
                            element={
                              <HRManagerDashboard
                                permissionKey={
                                  PagePermissionNameData?.HR_SETTINGS
                                }
                              />
                            }
                          />
                          <Route
                            path="hr-settings/manage-time-off"
                            element={<ManageTimeOff />}
                          />
                          <Route
                            path="manage-time-off"
                            element={<Navigate to="/hr-settings/manage-time-off" replace />}
                          />
                          <Route
                            path="hr-settings/auto-clock-out"
                            element={<AutoClockOut />}
                          />
                          <Route
                            path="hr-settings/payroll"
                            element={<Payroll />}
                          />
                          <Route
                            path="hr-settings/payroll/calendar"
                            element={<HRDashboardClockedHours />}
                          />
                          <Route
                            path="hr-settings/manage-holidays"
                            element={<ManageHolidays />}
                          />
                          <Route
                            path="hr-settings/manage-allowance"
                            element={<ManageAllowance />}
                          />
                          <Route
                            path="hr-settings/payroll-rules"
                            element={<PayrollRules />}
                          />
                          <Route
                            path="hr-settings/events"
                            element={<Events />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="stock_manager" />}
                        >
                          <Route
                            path="stock-manager/stocks"
                            element={<StockList />}
                          />
                          <Route
                            path="stock-manager/stocks/:productTypeId"
                            element={<StockList />}
                          />
                          <Route
                            path="stock-manager/stock/create"
                            element={<CreateStock />}
                          />
                          <Route
                            path="stock-manager/stock/edit/:_id"
                            element={<CreateStock />}
                          />
                          <Route
                            path="stock-manager/stock/view/:_id"
                            element={<ViewStock />}
                          />
                          <Route
                            path="stock-manager/purchase-orders"
                            element={<PurchaseOrders />}
                          />
                          <Route
                            path="stock-manager/purchase-orders/:productTypeId"
                            element={<PurchaseOrders />}
                          />
                          <Route
                            path="stock-manager/purchase-orders/details/:id"
                            element={<PoDetails />}
                          />
                          <Route
                            path="stock-manager/suppliers"
                            element={<Suppliers />}
                          />
                          <Route
                            path="stock-manager/suppliers/:productTypeId"
                            element={<Suppliers />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="process_manager" />}
                        >
                          <Route
                            path="process-manager/process"
                            element={<SPMProcesses />}
                          />
                          <Route
                            path="process-manager/process/create"
                            element={<SPMCreateProcesses />}
                          />
                          <Route
                            path="process-manager/process/create/:_id"
                            element={<SPMCreateProcesses />}
                          />
                          <Route
                            path="process-manager/process/duplicate/:_id"
                            element={<SPMCreateProcesses />}
                          />
                          <Route
                            path="process-manager/component"
                            element={<SPMComponents />}
                          />
                          <Route
                            path="process-manager/component/create"
                            element={<SPMCreateComponents />}
                          />
                          <Route
                            path="process-manager/component/create/:_id"
                            element={<SPMCreateComponents />}
                          />
                          <Route
                            path="process-manager/component/duplicate/:_id"
                            element={<SPMCreateComponents />}
                          />
                          <Route
                            path="process-manager/part"
                            element={<SPMParts />}
                          />
                          <Route
                            path="process-manager/part/create"
                            element={<SPMCreateParts />}
                          />
                          <Route
                            path="process-manager/part/create/:_id"
                            element={<SPMCreateParts />}
                          />
                          <Route
                            path="process-manager/part/duplicate/:_id"
                            element={<SPMCreateParts />}
                          />
                          <Route
                            path="process-manager/product-bom"
                            element={<SPMProductBOM />}
                          />
                          <Route
                            path="process-manager/product-bom/create"
                            element={<SPMCreateProductBOM />}
                          />
                          <Route
                            path="process-manager/product-bom/create/:_id"
                            element={<SPMCreateProductBOM />}
                          />
                          <Route
                            path="process-manager/product-bom/duplicate/:_id"
                            element={<SPMCreateProductBOM />}
                          />
                          <Route
                            path="process-manager/product-bom/view/:_id"
                            element={<SPMViewProductBOM />}
                          />
                          <Route
                            path="process-manager/product-bom/import/:_id"
                            element={<SPMImportBOM />}
                          />
                          <Route
                            path="process-manager/bom-search"
                            element={<BOMSearch />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="manage_load_notice" />
                          }
                        >
                          <Route
                            path="manage-load-notice/adjustable-pallet-racking"
                            element={<AdjustablePalletRacking />}
                          />
                          <Route
                            path="manage-load-notice/adjustable-pallet-racking/create"
                            element={<CreateAdjustablePalletRacking />}
                          />
                          <Route
                            path="manage-load-notice/adjustable-pallet-racking/edit/:_id"
                            element={<CreateAdjustablePalletRacking />}
                          />
                          <Route
                            path="manage-load-notice/adjustable-pallet-racking/view/:_id"
                            element={<ViewAdjustablePalletRacking />}
                          />
                          <Route
                            path="manage-load-notice/adjustable-pallet-racking/duplicate/:_id"
                            element={<CreateAdjustablePalletRacking />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="load_notice_creator" />
                          }
                        >
                          <Route
                            path="load-notice-creator/apr-load-notice"
                            element={<APRLoadNoticeCreator />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="steel_coil_manager" />
                          }
                        >
                          <Route
                            path="steel-coil-manager"
                            element={<SteelCoilManager />}
                          />
                          {/* <Route
                            path="steel-coil-manager/:id"
                            element={<SteelCoilDetail />}
                          /> */}
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="notification_email_templates" />
                          }
                        >
                          <Route
                            path="notification-email-templates"
                            element={<NotificationEmailTemplates />}
                          />
                          <Route
                            path="notification-email-templates/edit/:id"
                            element={<EditNotificationEmailTemplates />}
                          />
                          <Route
                            path="notification-email-templates/view/:id"
                            element={<ViewNotificationEmailTemplateDetails />}
                          />
                        </Route>
                        <Route
                          element={
                            <AdminIndex moduleName="roles_permissions" />
                          }
                        >
                          <Route
                            path="roles-and-permissions"
                            element={<RolesAndPermissions />}
                          />
                          <Route
                            path="roles-and-permissions/create"
                            element={<EditRolesAndPermissions />}
                          />
                          <Route
                            path="roles-and-permissions/edit/:roleId"
                            element={<EditRolesAndPermissions />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="static_pages" />}
                        >
                          <Route
                            path="static-pages"
                            element={<StaticPages />}
                          />
                          <Route
                            path="static-pages/edit-page/:url"
                            element={<EditStaticPage />}
                          />
                        </Route>
                        <Route
                          element={<AdminIndex moduleName="master_data" />}
                        >
                          <Route
                            path="server-queue-list"
                            element={<ServerQueueList />}
                          />
                        </Route>
                        <Route element={<AdminIndex moduleName="sage" />}>
                          <Route path="sage" element={<SageCallback />} />
                          <Route
                            path="sage/test-form"
                            element={<TestSageData />}
                          />
                        </Route>
                        {/* <Route element={<AdminIndex moduleName="static_data" />}>
                        <Route path="static-data" element={<StaticData />} />
                      </Route> */}

                        <Route path="*" element={<PageNotFound />} />
                        <Route path="/crypto" element={<CryptoFunction />} />
                        <Route path="*" element={<PageNotFound />} />
                      </Routes>
                      <AttendanceSyncHandler />
                    </SocketProvider>
                  </ExceptionBoundary>
                  <AppUpdateModal
                    isModalOpen={isAnyUpdateModalVisible}
                    isStoreUpdate={isStoreUpdateFlowActive}
                    isInAppUpdate={isInAppUpdateFlowActive}
                    isApkUpdate={isApkUpdateFlowActive}
                    handlePress={
                      isApkUpdateFlowActive ? handleApkUpdate : handleUpdate
                    }
                    handleClose={handleCloseAppUpdateModal}
                    updateInfo={
                      isApkUpdateFlowActive ? apkUpdateInfo : updateInfo
                    }
                  />
                  {(updateProgress > 0 || isApkUpdating) && (
                    <FullScreenProgress
                      progress={isApkUpdating ? apkProgress : updateProgress}
                      title={
                        isApkUpdating
                          ? apkPhase === "downloading"
                            ? "Downloading Update"
                            : apkPhase === "installing"
                              ? "Installing Update"
                              : "Completing Update"
                          : "App Update in Progress"
                      }
                      message={
                        isApkUpdating
                          ? apkPhase === "downloading"
                            ? "Downloading the update. Please keep the app open while it downloads."
                            : apkPhase === "installing"
                              ? "The update has been downloaded. Android is preparing the installation. Please keep the app open."
                              : "Android is finishing the installation. If prompted, confirm the installation. The app will reopen when complete."
                          : undefined
                      }
                      downloadedMb={
                        isApkUpdating && apkProgressInfo.bytesWritten
                          ? apkProgressInfo.bytesWritten / (1024 * 1024)
                          : undefined
                      }
                      totalMb={
                        isApkUpdating && apkProgressInfo.totalBytes
                          ? apkProgressInfo.totalBytes / (1024 * 1024)
                          : undefined
                      }
                    />
                  )}
                </Suspense>
                <DeepLinkingHandler />
              </PersistGate>
            </BrowserRouter>
            <ToastHandler />
          </Provider>
        </QueryClientProvider>
      </AppWrapper>
    </ConfigProvider>
  );
}

export default App;
